import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatTableDataSource } from '@angular/material/table';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';
import { GroupToAgentDialogComponent } from 'src/app/modals/group-to-agent-dialog/group-to-agent-dialog.component';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { AgentGroup, AgentGroupRecordHistory } from 'src/app/models/agentGroup.model';
import { AgentGroupOrgLevel } from 'src/app/models/agentGroupOrglevel.model';
import { AgentLookup } from 'src/app/models/agentLookup.model';
import { Lookup } from 'src/app/models/lookup.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { AgentGroupOrgLevelService } from 'src/app/services/agent-group-org-level.service';
import { AgentService } from 'src/app/services/agent.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { buildHistoryParams, getHistoryDateTimeDisplay, loadPopupHistory } from 'src/app/_helpers/history-popup.helper';
import { Subject, takeUntil } from 'rxjs';

const ACTIONS_COLUMN = 'actions';

@Component({
  standalone: false,
  selector: 'app-agent-group-details-popup',
  templateUrl: './agent-group-details-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './agent-group-details-popup.component.scss'
})
export class AgentGroupDetailsPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  agentGroupDetails!: AgentGroup;
  displayedColumns: string[] = [];
  displayedAssociatedColumns: string[] = [];
  dataSource = new MatTableDataSource<Lookup>();
  orgLevelDataSource = new MatTableDataSource<AgentGroupOrgLevel>();
  historyDisplayedColumns: string[] = [];
  historyDataSource = new MatTableDataSource<AgentGroupRecordHistory>();
  private allAgents: AgentLookup[] | null = null;
  private readonly destroy$ = new Subject<void>();
  private historyLoaded = false;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  isLoading = false;
  isHistoryLoading = false;

  constructor(
    @Inject(FLYOUT_DATA) public data: string,
    private agentGroupService: AgentGroupService,
    private agentService: AgentService,
    private organizationLevelService: OrganizationLevelService,
    private agentGroupOrgLevelService: AgentGroupOrgLevelService,
    private toastService: ToastService,
    private dateFormatService: DateFormatService,
    protected dialog: MatDialog
  ) { }

  ngOnInit(): void {
    if (this.data) {
      this.getAgentGroupDetails();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAgentGroupDetails() {
    this.isLoading = true;
    this.agentGroupService.getById(this.data).subscribe({
      next: (response) => {
        this.agentGroupDetails = response;
        this.getOrgLevelParentId();
        this.setColumns();
        this.loadGroupAgents();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }

  setColumns() {
    this.displayedColumns = [
      nameOf<Lookup>(x => x.name),
      ACTIONS_COLUMN,
    ];
    this.displayedAssociatedColumns = [
      nameOf<AgentGroupOrgLevel>(x => x.organizationLevelName),
    ];
    this.historyDisplayedColumns = [
      nameOf<AgentGroupRecordHistory>(x => x.name),
      nameOf<AgentGroupRecordHistory>(x => x.casId),
      nameOf<AgentGroupRecordHistory>(x => x.organizationLevelName),
      nameOf<AgentGroupRecordHistory>(x => x.dateUpdated),
      nameOf<AgentGroupRecordHistory>(x => x.updatedByUserName)
    ];
  }

  getHistory(): void {
    this.isHistoryLoading = true;
    const params = buildHistoryParams('agentGroupId', this.data);

    loadPopupHistory(
      this.agentGroupService.getHistory(params),
      this.destroy$,
      (history) => this.mapHistoryRecord(history),
      (records) => {
        this.historyDataSource.data = records;
        this.historyLoaded = true;
        this.isHistoryLoading = false;
      },
      (error) => {
        this.toastService.showError(error);
        this.historyDataSource.data = [];
        this.historyLoaded = false;
        this.isHistoryLoading = false;
      },
    );
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 2 && !this.historyLoaded) {
      this.getHistory();
    }
  }

  private mapHistoryRecord(history: AgentGroupRecordHistory): AgentGroupRecordHistory {
    return {
      ...history,
      changedFieldSet: new Set(getHistoryChangedFields(history)),
      dateUpdatedDisplay: getHistoryDateTimeDisplay(history.dateUpdated, this.dateFormatService),
    };
  }

  getOrgLevelParentId() {
    this.isLoading = true;
    this.organizationLevelService.getParentSite(this.agentGroupDetails.organizationLevelId).subscribe({
      next: (response) => {
        if (!response) {
          this.getAgentGroupOrgLabels(this.data);
          return;
        }
        const { parentId, orgLevelType } = response;
        if (parentId == null) {
          this.getAgentGroupOrgLabels(this.data);
          return;
        }
        this.getAgentGroupOrgLabels(this.data, parentId, orgLevelType);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });

  }

  getAgentGroupOrgLabels(agentGroupId: string, parentOrgLevelId?: string, orgLevelType?: number): void {
    this.isLoading = true;
    this.agentGroupOrgLevelService.getAssociations(agentGroupId, parentOrgLevelId, orgLevelType).subscribe({
      next: (response) => {
        this.orgLevelDataSource.data = response;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isLoading = false;
      }
    });
  }

  openRemoveAgentDialog(id?: string) {
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        id: id,
        title: 'Agent from this Group',
        customDelete: () => this.removeAgentFromGroup(id),
      },
      width: '440px',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: unknown) => {
        if (result) {
          this.getAgentGroupDetails();
        }
      }
    });
  }

  removeAgentFromGroup(agentId?: string) {
    if (!agentId) return;

    if (this.agentGroupDetails.agentIds) {
      const index = this.agentGroupDetails.agentIds.indexOf(agentId);
      if (index > -1) {
        this.agentGroupDetails.agentIds.splice(index, 1);
      }
    }

    return this.agentGroupService.update(this.agentGroupDetails);
  }

  // Builds the list of Agents that actually belong to this group from its
  // membership (agentIds), resolving display names from the agent lookup.
  // This intentionally replaces the previous jurisdiction-based query, which
  // returned one row per shared jurisdiction (causing duplicates) and went
  // blank whenever members had no jurisdiction in common.
  loadGroupAgents() {
    const agentIds = this.agentGroupDetails?.agentIds ?? [];
    if (!agentIds.length) {
      this.dataSource.data = [];
      this.isLoading = false;
      return;
    }

    if (this.allAgents) {
      this.setGroupAgents(this.allAgents, agentIds);
      return;
    }

    this.isLoading = true;
    this.agentService.getAll().subscribe({
      next: (agents) => {
        this.allAgents = agents;
        this.setGroupAgents(agents, agentIds);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.dataSource.data = [];
        this.isLoading = false;
      }
    });
  }

  private setGroupAgents(agents: AgentLookup[], agentIds: string[]) {
    const memberIds = new Set<string>(agentIds);
    this.dataSource.data = agents
      .filter((agent) => memberIds.has(agent.id))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    this.isLoading = false;
  }

  addAgentsToGroup(agentGroup: AgentGroup) {
    const agentGroupModel: IFormDialogData = agentGroup;

    const dialogRef = this.dialog.open(GroupToAgentDialogComponent, {
      data: agentGroupModel,
      width: '440px',
      disableClose: true
    });
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: unknown) => {
        if (result) { this.getAgentGroupDetails() }
      }
    });
  }

}
